<template>
  <div class="bg-white rounded-lg shadow-md border border-gray-200 p-6">
    <!-- Widget Header -->
    <div class="flex items-center justify-between mb-6">
      <div class="flex items-center space-x-3">
        <div
          class="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center"
        >
          <i class="material-icons text-white text-lg">person_add</i>
        </div>
        <div>
          <h3 class="text-lg font-semibold text-gray-900">
            Recently Registered Tenants
          </h3>
          <p class="text-sm text-gray-500">
            {{ totalCount }} tenants registered {{ periodText }}
          </p>
        </div>
      </div>
      <div class="flex items-center space-x-2">
        <!-- Period Filter -->
        <select
          v-model="selectedPeriod"
          @change="onPeriodChange"
          class="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
        >
          <option value="today">Today</option>
          <option value="this_week">This Week</option>
          <option value="this_month">This Month</option>
          <option value="last_year">Last Year</option>
        </select>
      </div>
    </div>

    <!-- Date Range Info -->
    <div v-if="dateRange" class="mb-4 p-3 bg-gray-50 rounded-lg">
      <div class="flex items-center justify-between text-sm">
        <span class="text-gray-600">Period:</span>
        <span class="font-medium text-gray-900">
          {{ formatDateRange(dateRange) }}
        </span>
      </div>
    </div>

    <!-- Statistics Summary -->
    <div
      v-if="recentTenants && recentTenants.length > 0"
      class="mb-6 grid grid-cols-2 md:grid-cols-4 gap-4"
    >
      <div class="text-center p-3 bg-gray-50 rounded-lg">
        <div class="text-lg font-semibold text-gray-900">
          {{ totalCount }}
        </div>
        <div class="text-xs text-gray-500">Total</div>
      </div>
      <div class="text-center p-3 bg-green-50 rounded-lg">
        <div class="text-lg font-semibold text-green-600">
          {{ getStatusCount("Active") }}
        </div>
        <div class="text-xs text-green-600">Active</div>
      </div>
      <div class="text-center p-3 bg-orange-50 rounded-lg">
        <div class="text-lg font-semibold text-orange-600">
          {{ getStatusCount("Non Renewing") }}
        </div>
        <div class="text-xs text-orange-600">Non Renewing</div>
      </div>
      <div class="text-center p-3 bg-red-50 rounded-lg">
        <div class="text-lg font-semibold text-red-600">
          {{ getStatusCount("Cancelled") }}
        </div>
        <div class="text-xs text-red-600">Cancelled</div>
      </div>
    </div>

    <!-- Data Table -->
    <DataTable
      title=""
      :columns="tableColumns"
      :data="recentTenants"
      :loading="loading"
      :show-pagination="true"
      :items-per-page="10"
      @refresh="refreshData"
      @export="exportData"
    />
  </div>
</template>

<script>
import DataTable from "./DataTable.vue";

export default {
  name: "RecentlyRegisteredTenantsWidget",
  components: {
    DataTable
  },
  props: {
    recentTenants: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    totalCount: {
      type: Number,
      default: 0
    },
    currentFilter: {
      type: String,
      default: "this_week"
    },
    dateRange: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      selectedPeriod: this.currentFilter
    };
  },
  computed: {
    periodText() {
      const periodMap = {
        today: "today",
        this_week: "this week",
        this_month: "this month",
        last_year: "last year"
      };
      return periodMap[this.selectedPeriod] || "in selected period";
    },

    tableColumns() {
      return [
        {
          key: "tenant_name",
          label: "Tenant",
          type: "avatar",
          avatarName: "tenant_name",
          avatarInitials: true
        },
        {
          key: "email",
          label: "Email",
          type: "text"
        },
        {
          key: "subdomain",
          label: "Subdomain",
          type: "text"
        },
        {
          key: "plan_code",
          label: "Plan",
          type: "badge",
          badgeColors: {
            "starter-npr-monthly": "bg-blue-100 text-blue-800",
            "premium-usd-every-3-years": "bg-purple-100 text-purple-800",
            "basic-usd-monthly": "bg-green-100 text-green-800"
          }
        },
        {
          key: "status",
          label: "Status",
          type: "badge",
          badgeColors: {
            active: "bg-green-100 text-green-800",
            "non renewing": "bg-orange-100 text-orange-800",
            cancelled: "bg-red-100 text-red-800",
            trial: "bg-blue-100 text-blue-800",
            expired: "bg-gray-100 text-gray-800"
          }
        },
        {
          key: "country",
          label: "Country",
          type: "text"
        },
        {
          key: "registered_at",
          label: "Registered",
          type: "date"
        }
      ];
    }
  },

  watch: {
    currentFilter(newFilter) {
      this.selectedPeriod = newFilter;
    }
  },

  methods: {
    onPeriodChange() {
      this.$emit("period-changed", this.selectedPeriod);
    },

    refreshData() {
      this.$emit("refresh");
    },

    exportData() {
      this.generateExport();
    },

    formatDateRange(dateRange) {
      if (!dateRange) return "";

      const start = new Date(dateRange.start);
      const end = new Date(dateRange.end);

      const options = {
        year: "numeric",
        month: "short",
        day: "numeric"
      };

      return `${start.toLocaleDateString(
        "en-US",
        options
      )} - ${end.toLocaleDateString("en-US", options)}`;
    },

    getStatusCount(status) {
      return this.recentTenants.filter(
        tenant => tenant.status.toLowerCase() === status.toLowerCase()
      ).length;
    },

    formatPlanCode(planCode) {
      // Convert plan_code to readable format
      return planCode.replace(/-/g, " ").replace(/\b\w/g, l => l.toUpperCase());
    },

    generateExport() {
      if (!this.recentTenants || this.recentTenants.length === 0) {
        return;
      }

      // Generate CSV content
      const headers = [
        "Tenant Name",
        "Email",
        "Subdomain",
        "Plan",
        "Status",
        "Country",
        "Registered At",
        "Registration Date"
      ];

      let csvContent = headers.join(",") + "\n";

      this.recentTenants.forEach(tenant => {
        const row = [
          `"${tenant.tenant_name}"`,
          `"${tenant.email}"`,
          `"${tenant.subdomain}"`,
          `"${this.formatPlanCode(tenant.plan_code)}"`,
          `"${tenant.status}"`,
          `"${tenant.country}"`,
          `"${tenant.registered_at_human}"`,
          `"${new Date(tenant.registered_at).toLocaleDateString()}"`
        ];
        csvContent += row.join(",") + "\n";
      });

      // Add summary
      csvContent += "\n";
      csvContent += "SUMMARY\n";
      csvContent += `"Total Tenants",${this.totalCount}\n`;
      csvContent += `"Period","${this.periodText}"\n`;
      csvContent += `"Date Range","${this.formatDateRange(this.dateRange)}"\n`;
      csvContent += `"Active",${this.getStatusCount("Active")}\n`;
      csvContent += `"Non Renewing",${this.getStatusCount("Non Renewing")}\n`;
      csvContent += `"Cancelled",${this.getStatusCount("Cancelled")}\n`;

      // Download file
      const blob = new Blob(["\uFEFF" + csvContent], {
        type: "text/csv;charset=utf-8;"
      });

      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);

      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `recently-registered-tenants-${this.selectedPeriod}.csv`
      );
      link.style.visibility = "hidden";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);

      // Export completed successfully
    }
  }
};
</script>

<style scoped>
/* Animation for status badges */
.transition-colors {
  transition: background-color 0.2s ease-in-out, color 0.2s ease-in-out;
}
</style>
